import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { TaskGroupInterface } from '../../../interfaces/TaskInterface';
import Dropdown from '../../Dropdown/MainDropdown';
import DropdownButton from '../../Dropdown/DropdownButton';
import DropdownItem from '../../Dropdown/DropdownItem';
import { ProjectModel } from '../../../mobx/models/project';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';

// Interface for GroupDropdown component props
interface Props {
  onUpdateGroup?: (group: TaskGroupInterface) => void;
  isCreateTaskModal?: boolean;
  currentProject?: ProjectModel;
  value?: string;
}

const GroupDropdown: React.FC<Props> = (props) => {
  const { onUpdateGroup, isCreateTaskModal, value, currentProject } = props;

  //Mobx store variables
  const { taskStore } = rootStore;
  const { createTaskInput } = taskStore;

  //Other variable
  const dispatch = useDispatch();

  // Find the currently selected group from the project's task groups
  const selectedGroup = currentProject?.taskGroups.find((group) => group.id === value);

  /**
   * Effect to set default group when creating a new task
   * Sets the first group as default if no group is selected and groups exist
   */
  useEffect(() => {
    if (isCreateTaskModal && !selectedGroup && currentProject?.taskGroups && currentProject?.taskGroups?.length > 0) {
      createTaskInput?.setGroupId(currentProject?.taskGroups[0].id);
    }
  }, [dispatch, isCreateTaskModal, selectedGroup, currentProject, createTaskInput]);

  /**
   * Handles the selection of a task group
   * @param {TaskGroupInterface} taskGroup - The selected task group
   */
  const onClickGroup = useCallback(
    (taskGroup: TaskGroupInterface) => {
      if (isCreateTaskModal) {
        createTaskInput.setGroupId(taskGroup.id);
      } else if (onUpdateGroup) {
        onUpdateGroup(taskGroup);
        return;
      }
    },
    [createTaskInput, isCreateTaskModal, onUpdateGroup]
  );

  // Determines the visual variant of the dropdown based on context
  const renderPriorityVariant = useMemo(() => {
    if (isCreateTaskModal) return 'border';
    return 'hover-border';
  }, [isCreateTaskModal]);

  // Renders the list of available task groups
  const renderGroupList = useMemo(() => {
    return currentProject?.taskGroups.map((group: TaskGroupInterface) => (
      <DropdownItem
        label={group.name}
        key={group?.id}
        onClick={() => onClickGroup(group)}
        isSelected={selectedGroup?.id === group.id}
      />
    ));
  }, [currentProject?.taskGroups, onClickGroup, selectedGroup?.id]);

  return (
    <Dropdown
      content={
        <DropdownButton
          iconName={isCreateTaskModal ? 'group-icon' : ''}
          title={selectedGroup?.name || 'Group'}
          isActive={selectedGroup?.name ? true : false}
          iconTone='stroke'
          size={isCreateTaskModal ? 'small' : 'medium'}
          variant={renderPriorityVariant}
        />
      }
      trigger='click'
      modalTitle='Group'>
      {renderGroupList}
    </Dropdown>
  );
};
export default observer(GroupDropdown);
